import { Option } from "@inrev/core";
import { cn } from "@portal/components/lib/utils";
import { FC, useCallback, useId } from "react";
import {
	Area,
	AreaChart,
	ResponsiveContainer,
	Tooltip,
	type TooltipContentProps,
	XAxis,
	YAxis,
} from "recharts";

export type ChartDataPoint = Record<string, Option<string | number>>;

const CHART_MARGIN = { top: 5, right: 0, bottom: 0, left: 0 } as const;
const X_AXIS_PADDING = { left: 20, right: 20 } as const;
const Y_AXIS_DOMAIN = [0, "auto"] as const;
const TOOLTIP_CURSOR = { className: "stroke-bc-strong", strokeWidth: 1 } as const;
const TOOLTIP_WRAPPER_STYLE = { outline: "none" } as const;
const ACTIVE_DOT = {
	r: 5,
	strokeWidth: 2,
	className: "fill-current stroke-surface-base",
} as const;
const TOOLTIP_POSITION = { y: 0 } as const;
const X_AXIS_TICK = {
	className: "fill-tertiary text-[12px]",
	transform: "translate(0, 6)",
} as const;

type FsrTrendTooltipProps = {
	active?: boolean;
	label?: TooltipContentProps["label"];
	payload?: ReadonlyArray<{ value?: TooltipContentProps["payload"][number]["value"] }>;
	swatchClass: string;
};
export const FsrTrendTooltip: FC<FsrTrendTooltipProps> = ({
	active,
	payload,
	label,
	swatchClass,
}) => {
	if (!active || !payload || !payload.length) return null;
	return (
		<div
			className="rounded bg-surface-base shadow-md border border-bc-default px-3 py-2 text-[12px]"
			data-amp-mask
			data-testid="fsr_trend_tooltip"
		>
			<p className="font-medium text-primary mb-1">{label}</p>
			<div className="flex items-center gap-2">
				<span className={`h-2 w-2 rounded-full ${swatchClass}`} />
				<span className="text-secondary">Score</span>
				<span className="font-medium text-primary ml-2">{payload[0]?.value}</span>
			</div>
		</div>
	);
};

type FsrTrendChartProps = {
	data: ChartDataPoint[];
	delta: number | null;
};
export const FsrTrendChart: FC<FsrTrendChartProps> = ({ data, delta }) => {
	const color =
		Option.isSome(delta) && delta !== 0
			? delta > 0
				? "inrev-green"
				: "inrev-red"
			: "brand-accent";
	const swatchClass = `bg-${color}` as const;
	const gradientId = `fsr-trend-gradient-${useId().replace(/:/g, "")}`;

	const tooltip = useCallback(
		({ active, payload, label }: TooltipContentProps) => (
			<FsrTrendTooltip active={active} payload={payload} label={label} swatchClass={swatchClass} />
		),
		[swatchClass],
	);

	return (
		<div
			className={cn("h-full w-full", `text-${color}`)}
			data-amp-mask
			data-testid="fsr_trend_chart"
		>
			<ResponsiveContainer width="100%" height="100%">
				<AreaChart data={data} margin={CHART_MARGIN}>
					<defs>
						<linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
							<stop offset="5%" stopColor="currentColor" stopOpacity={0.4} />
							<stop offset="95%" stopColor="currentColor" stopOpacity={0} />
						</linearGradient>
					</defs>
					<XAxis
						dataKey="quarter"
						tick={X_AXIS_TICK}
						tickLine={false}
						axisLine={false}
						interval="preserveStartEnd"
						minTickGap={0}
						padding={X_AXIS_PADDING}
					/>
					<YAxis hide domain={Y_AXIS_DOMAIN} />
					<Tooltip
						content={tooltip}
						cursor={TOOLTIP_CURSOR}
						wrapperStyle={TOOLTIP_WRAPPER_STYLE}
						position={TOOLTIP_POSITION}
						isAnimationActive={false}
					/>
					<Area
						type="monotone"
						dataKey="score"
						stroke="currentColor"
						strokeWidth={2}
						strokeLinecap="round"
						strokeLinejoin="round"
						fill={`url(#${gradientId})`}
						dot={false}
						activeDot={ACTIVE_DOT}
						animationDuration={900}
					/>
				</AreaChart>
			</ResponsiveContainer>
		</div>
	);
};
